
import React from 'react';
import { Language, UserRole } from '../types';
import { TRANSLATIONS } from '../translations';
import { 
  BarChart2, MousePointer, Wallet, Activity, ArrowDown, Calendar, 
  Download, Filter, ChevronDown, Layers, ExternalLink, TrendingUp, Users, DollarSign
} from 'lucide-react';
import { 
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, 
  BarChart, Bar, Cell, LineChart, Line, Legend, ComposedChart
} from 'recharts';

interface AnalyticsDashboardProps {
  language: Language;
  userRole: UserRole;
  onBack: () => void;
}

export const AnalyticsDashboard: React.FC<AnalyticsDashboardProps> = ({ language, userRole, onBack }) => {
  const t = TRANSLATIONS[language];

  // --- Project View Data (Existing) ---
  const funnelData = [
    { name: t.impressions, value: 450000, fill: '#6366f1' }, // Indigo
    { name: t.clicks, value: 12500, fill: '#818cf8' },      // Light Indigo
    { name: t.connectedWallets, value: 3400, fill: '#34d399' }, // Emerald
    { name: t.onChainTx, value: 850, fill: '#10b981' },     // Dark Emerald
  ];

  const trendData = [
    { date: '10/01', clicks: 400, tx: 20 },
    { date: '10/05', clicks: 1200, tx: 80 },
    { date: '10/10', clicks: 900, tx: 65 },
    { date: '10/15', clicks: 2800, tx: 180 },
    { date: '10/20', clicks: 1500, tx: 110 },
    { date: '10/25', clicks: 3200, tx: 250 },
    { date: '10/30', clicks: 2500, tx: 145 },
  ];

  const campaignComparison = [
    { id: 1, kol: 'Crypto Wizard', campaign: 'SuperNova Launch', cost: 1500, clicks: 4500, tx: 120, roi: 3.5 },
    { id: 2, kol: 'Meme King', campaign: 'FrogCoin Viral', cost: 3000, clicks: 12000, tx: 45, roi: 0.8 },
    { id: 3, kol: 'Alpha Asia', campaign: 'GameFi Beta', cost: 1200, clicks: 2100, tx: 150, roi: 4.2 },
    { id: 4, kol: 'Tech Reviewer', campaign: 'Hardware Wallet', cost: 4000, clicks: 800, tx: 300, roi: 2.1 },
  ];

  // --- KOL View Data (New) ---
  const kolTrendData = [
    { date: 'Week 1', earnings: 1200, clicks: 3500 },
    { date: 'Week 2', earnings: 1800, clicks: 4200 },
    { date: 'Week 3', earnings: 1500, clicks: 3800 },
    { date: 'Week 4', earnings: 2400, clicks: 5500 },
  ];

  const trafficQualityData = [
    { name: 'Campaign A', clicks: 2000, conversions: 120 },
    { name: 'Campaign B', clicks: 4000, conversions: 80 }, // Viral but low conversion
    { name: 'Campaign C', clicks: 1500, conversions: 300 }, // High quality
    { name: 'Campaign D', clicks: 2500, conversions: 150 },
  ];

  const myGigs = [
    { id: 1, project: 'DeFi Swap Protocol', date: '2023-10-15', views: '45K', clicks: 1200, earnings: 1500, convRate: '2.5%' },
    { id: 2, project: 'NFT Marketplace Launch', date: '2023-10-20', views: '22K', clicks: 800, earnings: 800, convRate: '4.1%' },
    { id: 3, project: 'L2 Bridge', date: '2023-10-28', views: '18K', clicks: 450, earnings: 1200, convRate: '1.8%' },
  ];


  // --- Render Project View ---
  const renderProjectAnalytics = () => (
    <>
       {/* Top Stats Cards */}
       <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-slate-800 border border-slate-700 rounded-xl p-5 relative overflow-hidden group">
           <div className="absolute right-0 top-0 w-24 h-24 bg-indigo-500/10 rounded-bl-full -mr-4 -mt-4 transition-transform group-hover:scale-110"></div>
           <p className="text-slate-400 text-sm font-medium relative z-10">{t.totalClicks}</p>
           <h3 className="text-2xl font-bold text-white mt-1 relative z-10">24,580</h3>
           <div className="flex items-center gap-1 mt-2 text-xs text-emerald-400 relative z-10">
              <ArrowDown size={12} className="rotate-180" />
              <span>+12.5%</span>
              <span className="text-slate-500 ml-1">vs last month</span>
           </div>
           <MousePointer className="absolute right-4 bottom-4 text-slate-700 group-hover:text-indigo-500/20 transition-colors" size={24} />
        </div>

        <div className="bg-slate-800 border border-slate-700 rounded-xl p-5 relative overflow-hidden group">
           <div className="absolute right-0 top-0 w-24 h-24 bg-emerald-500/10 rounded-bl-full -mr-4 -mt-4 transition-transform group-hover:scale-110"></div>
           <p className="text-slate-400 text-sm font-medium relative z-10">{t.connectedWallets}</p>
           <h3 className="text-2xl font-bold text-white mt-1 relative z-10">3,420</h3>
           <div className="flex items-center gap-1 mt-2 text-xs text-emerald-400 relative z-10">
              <ArrowDown size={12} className="rotate-180" />
              <span>+8.2%</span>
           </div>
           <Wallet className="absolute right-4 bottom-4 text-slate-700 group-hover:text-emerald-500/20 transition-colors" size={24} />
        </div>

        <div className="bg-slate-800 border border-slate-700 rounded-xl p-5 relative overflow-hidden group">
           <div className="absolute right-0 top-0 w-24 h-24 bg-purple-500/10 rounded-bl-full -mr-4 -mt-4 transition-transform group-hover:scale-110"></div>
           <p className="text-slate-400 text-sm font-medium relative z-10">{t.onChainTx}</p>
           <h3 className="text-2xl font-bold text-white mt-1 relative z-10">854</h3>
           <div className="flex items-center gap-1 mt-2 text-xs text-emerald-400 relative z-10">
              <ArrowDown size={12} className="rotate-180" />
              <span>+24%</span>
           </div>
           <Layers className="absolute right-4 bottom-4 text-slate-700 group-hover:text-purple-500/20 transition-colors" size={24} />
        </div>

        <div className="bg-slate-800 border border-slate-700 rounded-xl p-5 relative overflow-hidden group">
           <div className="absolute right-0 top-0 w-24 h-24 bg-orange-500/10 rounded-bl-full -mr-4 -mt-4 transition-transform group-hover:scale-110"></div>
           <p className="text-slate-400 text-sm font-medium relative z-10">{t.txVolume}</p>
           <h3 className="text-2xl font-bold text-white mt-1 relative z-10">$450K</h3>
           <div className="flex items-center gap-1 mt-2 text-xs text-rose-400 relative z-10">
              <ArrowDown size={12} />
              <span>-2.1%</span>
           </div>
           <Activity className="absolute right-4 bottom-4 text-slate-700 group-hover:text-orange-500/20 transition-colors" size={24} />
        </div>
      </div>

      {/* Main Charts Area */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Trend Chart */}
        <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-lg font-bold text-white">{t.trendAnalysis}</h3>
            <div className="flex items-center gap-2">
                <span className="flex items-center gap-1 text-xs text-indigo-400"><div className="w-2 h-2 rounded-full bg-indigo-500"></div> {t.clicks}</span>
                <span className="flex items-center gap-1 text-xs text-emerald-400"><div className="w-2 h-2 rounded-full bg-emerald-500"></div> {t.onChainTx}</span>
            </div>
          </div>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={trendData}>
                <defs>
                  <linearGradient id="colorClicks" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#6366f1" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="colorTx" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" vertical={false} />
                <XAxis dataKey="date" stroke="#94a3b8" />
                <YAxis yAxisId="left" stroke="#94a3b8" />
                <YAxis yAxisId="right" orientation="right" stroke="#94a3b8" />
                <Tooltip contentStyle={{ backgroundColor: '#1e293b', borderColor: '#334155', color: '#f8fafc' }} />
                <Area yAxisId="left" type="monotone" dataKey="clicks" stroke="#6366f1" fillOpacity={1} fill="url(#colorClicks)" strokeWidth={2} />
                <Area yAxisId="right" type="monotone" dataKey="tx" stroke="#10b981" fillOpacity={1} fill="url(#colorTx)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Funnel Chart */}
        <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
          <h3 className="text-lg font-bold text-white mb-6">{t.conversionFunnel}</h3>
          <div className="h-72 flex items-center">
            <ResponsiveContainer width="100%" height="100%">
               <BarChart data={funnelData} layout="vertical" barSize={30} margin={{ left: 40, right: 40 }}>
                 <CartesianGrid strokeDasharray="3 3" stroke="#334155" horizontal={false} />
                 <XAxis type="number" hide />
                 <YAxis type="category" dataKey="name" stroke="#94a3b8" width={120} tick={{fontSize: 12}} />
                 <Tooltip 
                    cursor={{fill: 'transparent'}}
                    contentStyle={{ backgroundColor: '#1e293b', borderColor: '#334155', color: '#f8fafc' }}
                 />
                 <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                    {funnelData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                 </Bar>
               </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Attribution Table */}
      <div className="bg-slate-800 border border-slate-700 rounded-xl overflow-hidden">
        <div className="p-6 border-b border-slate-700 flex justify-between items-center">
             <h3 className="text-lg font-bold text-white">{t.campaignComparison}</h3>
             <button className="text-indigo-400 hover:text-indigo-300 text-sm font-medium flex items-center gap-1">
                {t.viewReport} <ExternalLink size={14} />
             </button>
        </div>
        <div className="overflow-x-auto">
           <table className="w-full text-left">
             <thead className="bg-slate-900/50 text-slate-400 text-xs uppercase tracking-wider">
               <tr>
                 <th className="px-6 py-4">{t.kolColumn}</th>
                 <th className="px-6 py-4">{t.campaignColumn}</th>
                 <th className="px-6 py-4">{t.cost}</th>
                 <th className="px-6 py-4">{t.clicks}</th>
                 <th className="px-6 py-4">{t.onChainTx}</th>
                 <th className="px-6 py-4">{t.cpa}</th>
                 <th className="px-6 py-4">{t.campaignRoi}</th>
               </tr>
             </thead>
             <tbody className="divide-y divide-slate-700">
               {campaignComparison.map((row) => (
                 <tr key={row.id} className="hover:bg-slate-700/30 transition-colors">
                   <td className="px-6 py-4 font-medium text-white">{row.kol}</td>
                   <td className="px-6 py-4 text-slate-300 text-sm">{row.campaign}</td>
                   <td className="px-6 py-4 text-slate-300">${row.cost}</td>
                   <td className="px-6 py-4 text-slate-300">{row.clicks.toLocaleString()}</td>
                   <td className="px-6 py-4 text-emerald-400 font-medium">{row.tx}</td>
                   <td className="px-6 py-4 text-slate-300">${(row.cost / row.tx).toFixed(2)}</td>
                   <td className="px-6 py-4">
                     <span className={`px-2 py-1 rounded text-xs font-bold ${
                        row.roi >= 3 ? 'bg-emerald-500/10 text-emerald-400' : 
                        row.roi >= 1.5 ? 'bg-blue-500/10 text-blue-400' : 'bg-rose-500/10 text-rose-400'
                     }`}>
                        {row.roi}x
                     </span>
                   </td>
                 </tr>
               ))}
             </tbody>
           </table>
        </div>
      </div>
    </>
  );

  // --- Render KOL View ---
  const renderKolAnalytics = () => (
    <>
      {/* KOL Top Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-slate-800 border border-slate-700 rounded-xl p-5 relative overflow-hidden group">
           <div className="absolute right-0 top-0 w-24 h-24 bg-emerald-500/10 rounded-bl-full -mr-4 -mt-4 transition-transform group-hover:scale-110"></div>
           <p className="text-slate-400 text-sm font-medium relative z-10">{t.totalEarningsMonth}</p>
           <h3 className="text-2xl font-bold text-white mt-1 relative z-10">$6,900</h3>
           <div className="flex items-center gap-1 mt-2 text-xs text-emerald-400 relative z-10">
              <ArrowDown size={12} className="rotate-180" />
              <span>+15.2%</span>
           </div>
           <DollarSign className="absolute right-4 bottom-4 text-slate-700 group-hover:text-emerald-500/20 transition-colors" size={24} />
        </div>

        <div className="bg-slate-800 border border-slate-700 rounded-xl p-5 relative overflow-hidden group">
           <div className="absolute right-0 top-0 w-24 h-24 bg-indigo-500/10 rounded-bl-full -mr-4 -mt-4 transition-transform group-hover:scale-110"></div>
           <p className="text-slate-400 text-sm font-medium relative z-10">{t.linkClicks}</p>
           <h3 className="text-2xl font-bold text-white mt-1 relative z-10">17,450</h3>
           <div className="flex items-center gap-1 mt-2 text-xs text-emerald-400 relative z-10">
              <ArrowDown size={12} className="rotate-180" />
              <span>+5.4%</span>
           </div>
           <MousePointer className="absolute right-4 bottom-4 text-slate-700 group-hover:text-indigo-500/20 transition-colors" size={24} />
        </div>

        <div className="bg-slate-800 border border-slate-700 rounded-xl p-5 relative overflow-hidden group">
           <div className="absolute right-0 top-0 w-24 h-24 bg-pink-500/10 rounded-bl-full -mr-4 -mt-4 transition-transform group-hover:scale-110"></div>
           <p className="text-slate-400 text-sm font-medium relative z-10">{t.avgCpm}</p>
           <h3 className="text-2xl font-bold text-white mt-1 relative z-10">$18.50</h3>
           <div className="flex items-center gap-1 mt-2 text-xs text-slate-500 relative z-10">
              <span>--</span>
           </div>
           <TrendingUp className="absolute right-4 bottom-4 text-slate-700 group-hover:text-pink-500/20 transition-colors" size={24} />
        </div>

        <div className="bg-slate-800 border border-slate-700 rounded-xl p-5 relative overflow-hidden group">
           <div className="absolute right-0 top-0 w-24 h-24 bg-blue-500/10 rounded-bl-full -mr-4 -mt-4 transition-transform group-hover:scale-110"></div>
           <p className="text-slate-400 text-sm font-medium relative z-10">{t.onChainTx}</p>
           <h3 className="text-2xl font-bold text-white mt-1 relative z-10">450</h3>
           <div className="flex items-center gap-1 mt-2 text-xs text-emerald-400 relative z-10">
              <ArrowDown size={12} className="rotate-180" />
              <span>Verified</span>
           </div>
           <Layers className="absolute right-4 bottom-4 text-slate-700 group-hover:text-blue-500/20 transition-colors" size={24} />
        </div>
      </div>

      {/* KOL Charts Area */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Earnings & Traffic Mix */}
          <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
              <h3 className="text-lg font-bold text-white mb-6">{t.earningsGrowth}</h3>
              <div className="h-72">
                 <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={kolTrendData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#334155" vertical={false} />
                        <XAxis dataKey="date" stroke="#94a3b8" />
                        <YAxis yAxisId="left" stroke="#94a3b8" />
                        <YAxis yAxisId="right" orientation="right" stroke="#94a3b8" />
                        <Tooltip contentStyle={{ backgroundColor: '#1e293b', borderColor: '#334155', color: '#f8fafc' }} />
                        <Legend />
                        <Bar yAxisId="left" dataKey="earnings" name="Earnings ($)" fill="#10b981" radius={[4, 4, 0, 0]} barSize={30} />
                        <Line yAxisId="right" type="monotone" dataKey="clicks" name="Clicks" stroke="#818cf8" strokeWidth={3} />
                    </ComposedChart>
                 </ResponsiveContainer>
              </div>
          </div>

          {/* Traffic Quality Scatter/Bar */}
          <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
              <h3 className="text-lg font-bold text-white mb-6">{t.trafficQuality}</h3>
              <div className="h-72">
                 <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={trafficQualityData} layout="vertical" margin={{ left: 20 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#334155" horizontal={false} />
                        <XAxis type="number" stroke="#94a3b8" />
                        <YAxis type="category" dataKey="name" stroke="#94a3b8" width={100} tick={{fontSize: 12}} />
                        <Tooltip contentStyle={{ backgroundColor: '#1e293b', borderColor: '#334155', color: '#f8fafc' }} />
                        <Legend />
                        <Bar dataKey="clicks" name="Total Clicks" fill="#334155" radius={[0, 4, 4, 0]} />
                        <Bar dataKey="conversions" name="Verified Conversions" fill="#34d399" radius={[0, 4, 4, 0]} />
                    </BarChart>
                 </ResponsiveContainer>
              </div>
          </div>
      </div>

      {/* Recent Gigs Table */}
      <div className="bg-slate-800 border border-slate-700 rounded-xl overflow-hidden">
        <div className="p-6 border-b border-slate-700 flex justify-between items-center">
             <h3 className="text-lg font-bold text-white">{t.recentGigs}</h3>
             <button className="text-indigo-400 hover:text-indigo-300 text-sm font-medium flex items-center gap-1">
                {t.viewReport} <ExternalLink size={14} />
             </button>
        </div>
        <div className="overflow-x-auto">
           <table className="w-full text-left">
             <thead className="bg-slate-900/50 text-slate-400 text-xs uppercase tracking-wider">
               <tr>
                 <th className="px-6 py-4">{t.campaignColumn}</th>
                 <th className="px-6 py-4">{t.dueDateColumn}</th>
                 <th className="px-6 py-4">{t.views}</th>
                 <th className="px-6 py-4">{t.linkClicks}</th>
                 <th className="px-6 py-4">{t.conversionRate}</th>
                 <th className="px-6 py-4">{t.totalEarningsMonth}</th>
               </tr>
             </thead>
             <tbody className="divide-y divide-slate-700">
               {myGigs.map((row) => (
                 <tr key={row.id} className="hover:bg-slate-700/30 transition-colors">
                   <td className="px-6 py-4 font-medium text-white">{row.project}</td>
                   <td className="px-6 py-4 text-slate-400 text-sm">{row.date}</td>
                   <td className="px-6 py-4 text-slate-300">{row.views}</td>
                   <td className="px-6 py-4 text-slate-300">{row.clicks.toLocaleString()}</td>
                   <td className="px-6 py-4">
                     <span className={`px-2 py-1 rounded text-xs font-bold ${
                        parseFloat(row.convRate) > 3 ? 'bg-emerald-500/10 text-emerald-400' : 'bg-slate-500/10 text-slate-400'
                     }`}>
                        {row.convRate}
                     </span>
                   </td>
                   <td className="px-6 py-4 text-emerald-400 font-bold">${row.earnings}</td>
                 </tr>
               ))}
             </tbody>
           </table>
        </div>
      </div>
    </>
  );

  return (
    <div className="space-y-6 animate-in fade-in duration-500 pb-20">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Activity className="text-indigo-400" /> {userRole === UserRole.PROJECT ? t.advancedAnalytics : t.myPerformance}
          </h1>
          <p className="text-slate-400 text-sm mt-1">
             {userRole === UserRole.PROJECT ? t.projectAnalyticsSubtitle : t.kolAnalyticsSubtitle}
          </p>
        </div>
        
        <div className="flex gap-2">
           <button className="flex items-center gap-2 px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-slate-300 text-sm hover:text-white">
              <Calendar size={14} />
              {t.dateRange}
              <ChevronDown size={14} />
           </button>
           {userRole === UserRole.PROJECT && (
                <button className="flex items-center gap-2 px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-slate-300 text-sm hover:text-white">
                    <Layers size={14} />
                    {t.linear}
                </button>
           )}
           <button className="flex items-center gap-2 px-3 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-medium transition-colors">
              <Download size={14} />
              {t.exportData}
           </button>
        </div>
      </div>

      {userRole === UserRole.PROJECT ? renderProjectAnalytics() : renderKolAnalytics()}

    </div>
  );
};
